import type { ReactElement, ReactNode } from 'react'

import { useUiContext } from '@orus.eu/pharaoh'

import { StandaloneFormMobileNavigationBar } from './components/standalone-form-mobile-navigation-bar.tsx'

import { GoBackButton } from '#/components/pages/subscription-v2/components/go-back-button.tsx'
import { SubscriptionStepLayout } from '#/components/pages/subscription-v2/components/subscription-step-layout.tsx'
import { SubscriptionV2Header } from '#/components/pages/subscription-v2/components/subscription-v2-header.tsx'
import { SubscriptionPageV2 } from '#/components/pages/subscription-v2/subscription-v2-page.tsx'
import { PharaohNextScope } from '#/lib/pharaoh-next/pharaoh-next-scope.tsx'

/**
 * The funnel's frame around a standalone form, as the exclusion page uses it: header with the form's own progress, back
 * button, the pharaoh-next content and next button, and a side column. The funnel rebuild replaces it.
 */
export function StandaloneFormShell({
  subscriptionId,
  progress,
  onBack,
  backDisabled = false,
  children,
  right,
  nextButton,
}: {
  /** Undefined while loading. */
  subscriptionId: string | undefined
  /** From 0 to 1. */
  progress: number
  /** Undefined hides the back button. */
  onBack: (() => void) | undefined
  backDisabled?: boolean
  children: ReactNode
  /** Below the content on mobile. */
  right: ReactNode
  /** Below the content on desktop, and in a bar with the back button on mobile. Undefined shows neither. */
  nextButton: ReactNode | undefined
}): ReactElement {
  const isDesktop = useUiContext() === 'desktop'
  return (
    <SubscriptionPageV2>
      <div className="flex h-full flex-col overflow-x-hidden overflow-y-scroll bg-background">
        <SubscriptionV2Header progress={progress} compact={false} subscriptionId={subscriptionId} />
        <SubscriptionStepLayout
          left={onBack ? <GoBackButton goBackToPreviousStep={onBack} disabled={backDisabled} /> : undefined}
          center={<PharaohNextScope>{children}</PharaohNextScope>}
          right={right}
          footer={
            isDesktop && nextButton !== undefined ? (
              <PharaohNextScope>
                <div className="mt-70 mb-50 flex justify-end">{nextButton}</div>
              </PharaohNextScope>
            ) : undefined
          }
        />
        {!isDesktop && nextButton !== undefined ? (
          <StandaloneFormMobileNavigationBar onBack={onBack} backDisabled={backDisabled}>
            {nextButton}
          </StandaloneFormMobileNavigationBar>
        ) : null}
      </div>
    </SubscriptionPageV2>
  )
}
